import { Component, computed, Injector, Input, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BaseModalComponent } from '@card-software/library';
import { Group } from '../../../models/user-manager/group';
import { FormControl, Validators } from '@angular/forms';
import { GroupProxyService } from '../../../proxies/group.proxy.service';
import { MessageService } from 'primeng/api';
import { Role } from '../../../models/user-manager/roles';
import { RolesProxyService } from '../../../proxies/roles.proxy.service';
import { AccessType } from '../../../models/user-manager/access-type';
import { ACCESS_TYPE_LABELS, AccessTypeLabel } from '../../../models/user-manager/access-type-labels';

@Component({
  selector: 'app-user-set-groups',
  templateUrl: './user-set-groups.component.html',
  styleUrl: './user-set-groups.component.scss',
})
export class UserSetGroupsComponent extends BaseModalComponent<Group | null> implements OnInit {
  @Input() group: Group | null = null;

  isUpdate = signal(false);
  listRoles = signal<Role[]>([]);

  readonly typeOptions: AccessTypeLabel[] = ACCESS_TYPE_LABELS;

  name = new FormControl('', [Validators.required]);
  description = new FormControl('', [Validators.required]);
  roles = new FormControl([] as string[], [Validators.required]);
  /** Standard or Global (#6263): the group only holds roles of its own type. */
  type = new FormControl<AccessType>(AccessType.Standard, { nonNullable: true });
  /** "Visible to external managers": delegated user managers can assign it. Only Standard groups. */
  isDelegable = new FormControl<boolean>(false, { nonNullable: true });

  private readonly selectedType = toSignal(this.type.valueChanges, { initialValue: this.type.value });
  readonly isGlobal = computed(() => this.selectedType() === AccessType.Global);
  /** The role picker offers only roles of the group's type (a missing type is Standard). */
  readonly rolesOfType = computed(() => this.listRoles().filter((role) => (role.type ?? AccessType.Standard) === this.selectedType()));

  constructor(
    injector: Injector,
    private groupProxyService: GroupProxyService,
    private messageService: MessageService,
    private rolesProxyService: RolesProxyService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.rolesProxyService.getAllRoles().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.listRoles.set(response.data);
        }
      },
      error: (error) => {
        throw error;
      },
    });

    if (this.group) {
      this.isUpdate.set(true);
      this.setInitialData(this.group);
    }
  }

  setInitialData(group: Group) {
    this.name.setValue(group.name ?? '');
    this.description.setValue(group.description ?? '');
    this.roles.setValue(group.roleIds ?? []);
    this.type.setValue(group.type ?? AccessType.Standard);
    this.isDelegable.setValue(group.isDelegable ?? false);
    this.syncVisibility();
  }

  /** Changing the type drops the roles of the other type (the API would refuse them) and, for Global, the visibility. */
  onTypeChange() {
    const offered = new Set(this.rolesOfType().map((role) => role.id));
    this.roles.setValue((this.roles.value ?? []).filter((id) => offered.has(id)));
    this.syncVisibility();
  }

  private syncVisibility() {
    if (this.type.value === AccessType.Global) {
      this.isDelegable.setValue(false);
      this.isDelegable.disable();
    } else {
      this.isDelegable.enable();
    }
  }

  onClose() {
    this.close({ action: 'close', data: null });
  }

  onSubmit() {
    const group: Group = {
      id: this.group?.id ?? '',
      name: this.name.value ?? '',
      description: this.description.value ?? '',
      roleIds: this.roles.value ?? [],
      type: this.type.value,
      isDelegable: this.type.value === AccessType.Standard && this.isDelegable.value,
    };

    if (this.isUpdate()) {
      this.groupProxyService.updateGroup(group).subscribe({
        next: () => {
          this.close({ action: 'save', data: null });
          this.messageService.add({
            severity: 'secondary',
            summary: 'Success',
            detail: `Group '${group.name}' updated successfully`,
            key: 'main',
            life: 2500,
          });
        },
        error: (error) => {
          console.log('error', error);
        },
      });
      return;
    }

    this.groupProxyService.createGroup(group).subscribe({
      next: (response) => {
        this.close({ action: 'save', data: null });
        this.messageService.add({
          severity: 'secondary',
          summary: 'Success',
          detail: `Group '${response.data?.name}' created successfully`,
          key: 'main',
          life: 2500,
        });
      },
      error: (error) => {
        console.log('error', error);
      },
    });
  }
}
